import { useCallback, useEffect, useState } from "react";

/**
 * useTheme — 2-state theme hook
 *
 * 라이트(light) ↔ 다크(dark) 순환.
 * 초기값:
 *   1) localStorage("iggg-theme") 또는 ("theme")
 *   2) 없으면 prefers-color-scheme (light/dark)
 *   3) 그래도 모호하면 "dark"
 *
 * document.documentElement.dataset.theme 에 반영.
 * 페이지마다 같은 코드를 복붙하지 말고 이 hook만 사용한다.
 *
 * NOTE: PDF/표지/견적서 미리보기는 index.css 에서
 *   .ep-cover-v2 / .ep-root / .simple-preview-content / @media print 로 보호.
 *   여기서는 토글만 책임지면 됨.
 */

const VALID = new Set(["light", "dark"]);

function readInitialTheme() {
  try {
    if (typeof document === "undefined") return "dark";
    // 이미 부트스트랩에서 세팅돼 있으면 그 값 채택 (StrictMode 이중 마운트 / 페이지 간 일관성)
    const existing = document.documentElement.dataset.theme;
    if (VALID.has(existing)) return existing;

    const saved =
      localStorage.getItem("iggg-theme") ||
      localStorage.getItem("theme");
    if (VALID.has(saved)) return saved;

    if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
    return "dark";
  } catch {
    return "dark";
  }
}

/**
 * 앱 부트 시점에 1회 호출 — 첫 paint 전에 dataset.theme 적용 (FOUC 방지).
 * main.jsx 에서 import 후 호출.
 */
export function bootstrapTheme() {
  if (typeof document === "undefined") return;
  const t = readInitialTheme();
  document.documentElement.dataset.theme = t;
}

export default function useTheme() {
  const [theme, setTheme] = useState(readInitialTheme);

  // 다른 탭/페이지에서 토글했을 때 동기화
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e) => {
      if (e.key !== "iggg-theme" && e.key !== "theme") return;
      if (VALID.has(e.newValue)) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "light" ? "dark" : "light";
      try {
        localStorage.setItem("iggg-theme", next);
        // 레거시 키 호환 (기존 페이지 일부가 "theme" 읽음)
        localStorage.setItem("theme", next);
      } catch { /* private mode 등 — 무시 */ }
      return next;
    });
  }, []);

  return { theme, toggleTheme, setTheme };
}
